// Single source of truth for the "Same engine. Different questions." verticals.
// Consumed by the homepage section, the /verticals index, and each
// /verticals/:slug page. None of these are live — status is NEXT | EXPLORING.
//
// Maintenance rule (see brief): review quarterly. Promote at most one EXPLORING
// to NEXT per quarter; delete any card with no inbound interest after two.

import deforestationImg from '@/assets/verticals/deforestation-free-supply-chains.jpg'
import carbonImg from '@/assets/verticals/carbon-nature-mrv.jpg'
import evidenceImg from '@/assets/verticals/evidence-disputes.jpg'
import sustainabilityImg from '@/assets/verticals/sustainability-reporting-assurance.jpg'
import lendingImg from '@/assets/verticals/agricultural-lending-land-collateral.jpg'
import insuranceImg from '@/assets/verticals/crop-insurance-parametric-triggers.jpg'
import industrialImg from '@/assets/verticals/industrial-sites-supply-chains.jpg'
import renewableImg from '@/assets/verticals/renewable-asset-performance.jpg'
import turfImg from '@/assets/verticals/turf-courses-venues.jpg'
import indexHeroImg from '@/assets/verticals/index-hero.jpg'
import wheatImg from '@/assets/commodity-markets-wheat.jpg'

export type VerticalStatus = 'NEXT' | 'EXPLORING'

export interface Vertical {
  slug: string
  title: string
  status: VerticalStatus
  group: string
  teaser: string
  questions: string[]
  image: string
  imageAlt: string
}

export interface VerticalGroup {
  id: string
  title: string
  strapline: string
}

export const verticalsIntro = {
  heading: 'Same engine. Different questions.',
  body: [
    'Mutios is one Earth-observation engine with a different vocabulary for each domain it enters. We started with commodity markets because it demanded the hardest version of the problem: signals rigorous enough to take a position on.',
    'Everything below reuses that same foundation — the archive, the point-in-time discipline, the plain-language interface — and simply changes what the questions are about.',
  ],
  honesty: 'None of these are live yet. If one of them is your problem, tell us. Customer pull decides what we build next.',
}

export const verticalsHeroImage = { src: indexHeroImg, alt: 'Aerial view of a patchwork of cultivated fields' }

// The one live vertical, shown as the anchor of the index.
export const liveVertical = {
  slug: 'commodity-markets',
  href: '/verticals/commodity-markets',
  title: 'Commodity Markets',
  teaser: 'Backtestable satellite crop signals for EU and Black Sea wheat — point-in-time, versioned, and desk-ready.',
  image: wheatImg,
  imageAlt: 'A ripening brown wheat field under an open sky',
}

export const verticalGroups: VerticalGroup[] = [
  {
    id: 'proof-compliance',
    title: 'Proof & compliance',
    strapline: 'Where the deliverable is evidence, not insight.',
  },
  {
    id: 'risk-capital',
    title: 'Risk & capital',
    strapline: 'Adjacent to where we started, for buyers who price risk.',
  },
  {
    id: 'assets-operations',
    title: 'Assets & operations',
    strapline: "For teams responsible for physical things they can't visit often.",
  },
  {
    id: 'unexpected',
    title: 'And one you might not expect',
    strapline: 'The same crop science, somewhere else entirely.',
  },
]

export const verticals: Vertical[] = [
  {
    slug: 'deforestation-free-supply-chains',
    title: 'Deforestation-free supply chains',
    status: 'NEXT',
    group: 'proof-compliance',
    teaser:
      "EU rules require plot-level proof that cocoa, coffee, soy, palm, cattle and timber didn't come from cleared land. The hard part isn't detection — it's an audit trail that holds up years later.",
    questions: [
      'Was this cocoa plot forested at any point after the cut-off date?',
      'Can I produce the geolocation and historical evidence for every plot in this shipment?',
      'If an auditor asks in three years, does my proof still reconstruct exactly?',
    ],
    image: deforestationImg,
    imageAlt: 'Cleared land at the edge of dense tropical forest',
  },
  {
    slug: 'carbon-nature-mrv',
    title: 'Carbon & nature MRV',
    status: 'EXPLORING',
    group: 'proof-compliance',
    teaser:
      'Carbon and biodiversity claims are only worth what their verification is worth. Measurement that is versioned, reproducible and reconstructible to any past date is the missing ingredient.',
    questions: [
      'What was the canopy cover on this project boundary at the start date?',
      'Can every number in this verification be traced to the measurement that produced it?',
      'Does my baseline still reproduce if someone re-runs it next year?',
    ],
    image: carbonImg,
    imageAlt: 'Aerial view of an unbroken green forest canopy',
  },
  {
    slug: 'evidence-disputes',
    title: 'Evidence & disputes',
    status: 'EXPLORING',
    group: 'proof-compliance',
    teaser:
      "What did this site look like on a specific date — and can you prove the evidence wasn't assembled after the fact? Environmental claims, insurance disputes, property boundaries, regulatory enforcement.",
    questions: [
      'What did this parcel look like on the date the claim was filed?',
      "Can I show the imagery wasn't selected or assembled after the dispute began?",
      'Where exactly was the boundary before the disputed change?',
    ],
    image: evidenceImg,
    imageAlt: 'Aerial view of land parcels and field boundaries',
  },
  {
    slug: 'sustainability-reporting-assurance',
    title: 'Sustainability reporting assurance',
    status: 'EXPLORING',
    group: 'proof-compliance',
    teaser:
      'Corporate environmental disclosures need numbers an auditor will sign. Traceable measurement, not estimates.',
    questions: [
      'Can I give my auditor a measured figure instead of an estimate?',
      'Is every disclosed number reconstructible to a fixed point in time?',
      'Which sites changed materially over the reporting period?',
    ],
    image: sustainabilityImg,
    imageAlt: 'Green forested landscape seen from above',
  },
  {
    slug: 'agricultural-lending-land-collateral',
    title: 'Agricultural lending & land collateral',
    status: 'EXPLORING',
    group: 'risk-capital',
    teaser:
      'Verify that financed land is genuinely farmed, and value agricultural collateral consistently — with the evidence as it stood on the decision date.',
    questions: [
      'Is the land behind this loan actually being farmed?',
      'What was the state of this collateral on the day we approved the facility?',
      'Are we valuing comparable parcels on a consistent basis?',
    ],
    image: lendingImg,
    imageAlt: 'Aerial view of cultivated farmland divided into fields',
  },
  {
    slug: 'crop-insurance-parametric-triggers',
    title: 'Crop insurance & parametric triggers',
    status: 'EXPLORING',
    group: 'risk-capital',
    teaser:
      'Independent loss verification and index triggers built on measurement both sides can audit.',
    questions: [
      'Did conditions on this field actually cross the loss threshold?',
      'Can both the insurer and the insured audit the same measurement?',
      'What did the index read on the trigger date, exactly?',
    ],
    image: insuranceImg,
    imageAlt: 'Dry, cracked soil in a drought-stricken field',
  },
  {
    slug: 'industrial-sites-supply-chains',
    title: 'Industrial sites & supply chains',
    status: 'EXPLORING',
    group: 'assets-operations',
    teaser:
      "Construction progress, stockpile volumes, site change, vegetation encroachment — and whether a supplier's facility is actually running.",
    questions: [
      "Is this supplier's facility actually operating, or idle?",
      'How much has this construction site progressed since last quarter?',
      "What's the stockpile volume at this yard now versus a year ago?",
    ],
    image: industrialImg,
    imageAlt: 'Aerial view of a shipping port stacked with containers',
  },
  {
    slug: 'renewable-asset-performance',
    title: 'Renewable asset performance',
    status: 'EXPLORING',
    group: 'assets-operations',
    teaser:
      'Soiling, vegetation shading and degradation across solar portfolios; siting and performance context for wind.',
    questions: [
      'Which sites in the portfolio are losing yield to soiling or shading?',
      'Is vegetation encroaching on any of these solar arrays?',
      "How does this site's performance context compare across the fleet?",
    ],
    image: renewableImg,
    imageAlt: 'Rows of solar panels in a large solar farm from above',
  },
  {
    slug: 'turf-courses-venues',
    title: 'Turf, courses & venues',
    status: 'EXPLORING',
    group: 'unexpected',
    teaser:
      'The vegetation science that tracks a wheat field also tracks a golf course, a stadium pitch portfolio, or a resort’s snow cover — at far higher value per hectare.',
    questions: [
      'How is turf health trending across every course in the portfolio?',
      "Which venues show stress before it's visible from the ground?",
      "What's the snow cover across the resort this week versus prior seasons?",
    ],
    image: turfImg,
    imageAlt: 'Aerial view of a manicured green golf course',
  },
]

export function getVertical(slug: string): Vertical | undefined {
  return verticals.find((v) => v.slug === slug)
}

export function verticalsByGroup(groupId: string): Vertical[] {
  return verticals.filter((v) => v.group === groupId)
}

/** Options for the interest-form vertical dropdown: every card, plus escape hatch. */
export const verticalOptions: { value: string; label: string }[] = [
  { value: liveVertical.slug, label: `${liveVertical.title} (available now)` },
  ...verticals.map((v) => ({ value: v.slug, label: v.title })),
  { value: 'something-else', label: 'Something else' },
]

export const STATUS_LABEL: Record<VerticalStatus, string> = {
  NEXT: 'Next',
  EXPLORING: 'Exploring',
}
